import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Code, AlertTriangle } from "lucide-react";
import type { PageIssue, SchemaErrorDetails } from "@/types/technicalAudit";

interface SchemaErrorCardProps {
	issue: PageIssue;
}

export function SchemaErrorCard({ issue }: SchemaErrorCardProps) {
	const details = issue.issue_details as SchemaErrorDetails;

	return (
		<div className="space-y-4">
			<div className="flex items-start gap-3 rounded-xl p-4 border bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-800">
				<div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-red-100 dark:bg-red-900/40">
					<AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
				</div>
				<div className="flex-1">
					<h4 className="font-semibold text-red-900 dark:text-red-300">Schema Validation Error</h4>
					<p className="text-sm mt-1 text-red-700 dark:text-red-400">
						Structured data ({details.schema_type}) on this page has {details.errors.length} error{details.errors.length !== 1 ? "s" : ""}. Invalid schema markup may prevent rich results.
					</p>
				</div>
			</div>

			<Card className="border-border shadow-sm">
				<CardHeader className="pb-3">
					<CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
						<div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center">
							<Code className="w-4 h-4 text-blue-600 dark:text-blue-400" />
						</div>
						Schema Summary
					</CardTitle>
				</CardHeader>
				<CardContent className="pt-0 space-y-3">
					<div className="grid grid-cols-2 gap-3">
						<div className="p-3 bg-red-50 dark:bg-red-950/40 rounded-lg border border-red-200 dark:border-red-800 text-center">
							<p className="text-2xl font-bold text-red-700 dark:text-red-400">{details.errors.length}</p>
							<p className="text-xs text-red-600 dark:text-red-400 mt-1">Errors</p>
						</div>
						<div className="p-3 bg-blue-50 dark:bg-blue-950/40 rounded-lg border border-blue-200 dark:border-blue-800 text-center">
							<p className="text-sm font-bold text-blue-700 dark:text-blue-400">{details.schema_type}</p>
							<p className="text-xs text-blue-600 dark:text-blue-400 mt-1">Schema Type</p>
						</div>
					</div>

					{details.errors.length > 0 && (
						<div>
							<p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Error Details</p>
							<div className="space-y-1.5">
								{details.errors.slice(0, 5).map((err, idx) => (
									<div key={idx} className="p-2 bg-red-50 dark:bg-red-950/40 rounded border border-red-200 dark:border-red-800">
										<p className="text-sm text-red-700 dark:text-red-400">{err}</p>
									</div>
								))}
								{details.errors.length > 5 && (
									<p className="text-xs text-muted-foreground">
										+{details.errors.length - 5} more errors
									</p>
								)}
							</div>
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
